import { useState, useRef, useCallback, useEffect } from 'react';
const MAX_DURATION = 15; // seconds
/**
 * Pick the best available codec for voice recording.
 * Priority: opus in ogg (best quality/size), opus in webm, mp4 (iOS fallback).
 */
function pickMimeType() {
    const candidates = [
        'audio/ogg;codecs=opus',
        'audio/webm;codecs=opus',
        'audio/mp4',
        'audio/webm',
    ];
    for (const mime of candidates) {
        if (MediaRecorder.isTypeSupported(mime))
            return mime;
    }
    return '';
}
export function useAudioRecorder() {
    const [isRecording, setIsRecording] = useState(false);
    const [duration, setDuration] = useState(0);
    const [blob, setBlob] = useState(null);
    const mediaRecorder = useRef(null);
    const chunks = useRef([]);
    const timerRef = useRef();
    const startTime = useRef(0);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current)
                clearInterval(timerRef.current);
            if (mediaRecorder.current?.state === 'recording') {
                mediaRecorder.current.stop();
            }
            mediaRecorder.current?.stream?.getTracks().forEach((t) => t.stop());
        };
    }, []);
    const start = useCallback(async () => {
        try {
            chunks.current = [];
            setBlob(null);
            setDuration(0);
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: { ideal: 48000 },
                    channelCount: { ideal: 1 },
                    // Disable aggressive processing that kills voice clarity
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: true,
                },
            });
            const mimeType = pickMimeType();
            const recorder = new MediaRecorder(stream, {
                ...(mimeType ? { mimeType } : {}),
                audioBitsPerSecond: 256000, // 256 kbps — high quality voice
            });
            mediaRecorder.current = recorder;
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.current.push(e.data);
                }
            };
            recorder.onstop = () => {
                const finalMime = recorder.mimeType || mimeType || 'audio/webm';
                const audioBlob = new Blob(chunks.current, { type: finalMime });
                setBlob(audioBlob);
                stream.getTracks().forEach((t) => t.stop());
                if (timerRef.current)
                    clearInterval(timerRef.current);
            };
            // No timeslice — collect in one chunk for better codec efficiency
            recorder.start();
            startTime.current = Date.now();
            setIsRecording(true);
            // Duration timer
            timerRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTime.current) / 1000);
                setDuration(elapsed);
                if (elapsed >= MAX_DURATION) {
                    recorder.stop();
                    setIsRecording(false);
                }
            }, 200);
        }
        catch {
            // Microphone access denied or not available
            setIsRecording(false);
        }
    }, []);
    const stop = useCallback(() => {
        if (mediaRecorder.current?.state === 'recording') {
            mediaRecorder.current.stop();
        }
        setIsRecording(false);
        if (timerRef.current)
            clearInterval(timerRef.current);
    }, []);
    const reset = useCallback(() => {
        setBlob(null);
        setDuration(0);
        chunks.current = [];
    }, []);
    return { isRecording, duration, blob, start, stop, reset };
}
